/** Surface feed tabs — each maps to a `verification_status` feed filter. */
export type SurfaceTab = "review" | "skipped" | "accepted" | "rejected";

/** Statuses accepted by `?verification_status=` on the feed. */
export type SurfaceFeedVerificationStatus =
  | "pending_review"
  | "skipped"
  | "accepted"
  | "rejected";

/** Server verification statuses for surface assets. */
export type VerificationStatus =
  | SurfaceFeedVerificationStatus
  | "awaiting_suggestion";

export type SurfaceWriteAction = "accept" | "reject" | "skip" | "reprocess";

/** AI-proposed disposition on a surface asset (confirm or override). */
export type RecommendedDisposition = "accept" | "reject" | "skip";

export interface SurfaceProgress {
  pending_review: number;
  max_pending_review: number;
  pending_quota_remaining: number;
  awaiting_suggestion: number;
  accepted?: number;
  rejected?: number;
  skipped?: number;
  total_image_files?: number;
  last_updated?: string | null;
}

/** Client-side feed filters (sent as repeatable query params). */
export interface SurfaceFeedFilters {
  /** Include assets that have any of these tag ids (OR). */
  tagIds?: number[];
  /** Exclude assets that have any of these tag ids (OR). */
  excludeTagIds?: number[];
  /** Filter by file task id. */
  taskId?: number | null;
}

export interface PromoteSurfaceAsset {
  id: number;
  board?: number;
  file_id?: number | null;
  task_id?: number | null;
  verification_status: VerificationStatus;
  caption?: string | null;
  alt_text?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
  ai_observations?: string[];
  /** Tag ids currently on the asset (solution + non-solution). */
  tag_ids?: number[];
  /** Alternate key some payloads may use instead of `tag_ids`. */
  tags?: number[];
  /** AI recommendation — confirm or override with surface actions. */
  recommended_disposition?: RecommendedDisposition | null;
  /** 0..1 confidence, or null when unavailable. */
  overall_confidence?: number | null;
  disposition_rationale?: string | null;
  actioned_by?: number | null;
  actioned_by_name?: string | null;
  actioned_at?: string | null;
  promoted_via?: "ai_pipeline" | "surface" | "entity" | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
  visibility_warnings?: string[];
}

export interface SurfaceFeedResponse {
  assets: PromoteSurfaceAsset[];
  next_cursor: number | string | null;
  progress?: SurfaceProgress;
  tab?: string | null;
  verification_status?: SurfaceFeedVerificationStatus[] | null;
}

export interface SurfaceSolutionTag {
  id: number;
  /** MarketingTag.label from `/surface/solution-tags`. */
  label: string;
  is_solution?: boolean;
  type?: string;
  tag_group?: string | null;
  description?: string | null;
}

/** Non-problem tags for feed include/exclude filters. */
export type SurfaceFilterTag = SurfaceSolutionTag;

export interface AcceptSurfaceAssetBody {
  tag_ids: number[];
  caption?: string | null;
  alt_text?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
}

/** Server-side filter snapshot for cohort bulk (V2). */
export interface SurfaceBulkFilter {
  verification_status?: SurfaceFeedVerificationStatus[];
  tag_id?: number[];
  exclude_tag_id?: number[];
  task_id?: number | null;
}

/** Explicit-id bulk (legacy) or filter-defined cohort bulk (V2). */
export interface SurfaceBulkBody {
  action: Exclude<SurfaceWriteAction, "reprocess">;
  /** Legacy: explicit asset ids. Omit / empty when `use_filter` is true. */
  asset_ids?: number[];
  use_filter?: boolean;
  filter?: SurfaceBulkFilter;
  exclude_ids?: number[];
  /** When true, returns count/sample only — no mutations. */
  preview?: boolean;
  /** Required when action is accept (id-path); filter-path relies on per-asset tags. */
  tag_ids?: number[];
  caption?: string | null;
  alt_text?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
}

export interface SurfaceBulkResultItem {
  asset_id: number;
  ok: boolean;
  status?: number;
  error?: string;
  asset?: PromoteSurfaceAsset;
}

export interface SurfaceBulkResponse {
  mode?: "sync" | "async";
  action?: string;
  count?: number;
  results?: SurfaceBulkResultItem[];
  progress?: SurfaceProgress;
  job_id?: string | number;
  sample_asset_ids?: number[];
  exclude_ids?: number[];
}

export interface SurfaceBulkPreviewResponse {
  count: number;
  sample_asset_ids?: number[];
  exclude_ids?: number[];
}

export interface ReanalyzePendingBody {
  async?: boolean;
}

export interface ReanalyzePendingResponse {
  job_id?: string | number;
  mode?: "async" | "sync";
  count?: number;
}

export interface FetchSurfaceFeedParams {
  /**
   * Preferred filter (takes precedence over legacy `tab` on the server).
   * Repeatable: pass an array for OR.
   */
  verificationStatus: SurfaceFeedVerificationStatus | SurfaceFeedVerificationStatus[];
  limit?: number;
  cursor?: number | string | null;
  tagIds?: number[];
  excludeTagIds?: number[];
  taskId?: number | null;
}

/** Map UI tab → feed `verification_status` filter. */
export function verificationStatusForTab(tab: SurfaceTab): SurfaceFeedVerificationStatus {
  if (tab === "review") return "pending_review";
  if (tab === "skipped") return "skipped";
  return tab;
}

/** Stable empty filters for compare / default state. */
export const EMPTY_SURFACE_FEED_FILTERS: SurfaceFeedFilters = {
  tagIds: [],
  excludeTagIds: [],
  taskId: null,
};

export function hasActiveSurfaceFilters(filters: SurfaceFeedFilters | undefined): boolean {
  if (!filters) return false;
  return (
    (filters.tagIds?.length ?? 0) > 0 ||
    (filters.excludeTagIds?.length ?? 0) > 0 ||
    filters.taskId != null
  );
}
